import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  CircularProgress,
} from "@mui/material";
import awsRegions from "aws-regions";

import { postFeatureConfiguration } from "../../store/featureConfiguration/featureConfigurationSlice";

const EventInsightConfigFields = (props) => {
  const [role, setRole] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [errors, setErrors] = useState({});
  const { masterAccountNumber, onSuccess, masterAccountDetails, getMemberAccountNumbers } = props;
  console.log("🚀 ~ EventInsightConfigFields ~ getMemberAccountNumbers:", getMemberAccountNumbers())

  const validateRole = (value) => {
    if (!value) {
      return "Role is required";
    } else if (!/^arn:aws:iam::[0-9]{12}:role/.test(value)) {
      return "IAM Role must be in proper format";
    }
    return "";
  };

  const validateBucketName = (value) => {
    if (!value) {
      return "Bucket name is required";
    } else if (!/^arn:aws:s3:::[a-z0-9]([a-z0-9.-]{1,61}[a-z0-9])?$/.test(value)) {
      return "S3 bucket ARN must be in proper format";
    }
    return "";
  };

  const validateSelectedRegion = (value) => {
    if (!value) {
      return "AWS Region is required";
    }
    return "";
  };

  // Check if all fields are valid for enabling the Verify button
  const isFormValid = () => {
    return role && bucketName && selectedRegion && 
           !validateRole(role) && 
           !validateBucketName(bucketName) && 
           !validateSelectedRegion(selectedRegion);
  };

  const handleRoleChange = (e) => {
    const value = e.target.value;
    setRole(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      role: validateRole(value),
    }));
  };

  const handleBucketNameChange = (e) => {
    const value = e.target.value;
    setBucketName(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      bucketName: validateBucketName(value),
    }));
  };

  const handleRegionChange = (e) => {
    const value = e.target.value;
    setSelectedRegion(value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedRegion: validateSelectedRegion(value),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Validate all fields
    const roleError = validateRole(role);
    const bucketNameError = validateBucketName(bucketName);
    const selectedRegionError = validateSelectedRegion(selectedRegion);

    setErrors({
      role: roleError,
      bucketName: bucketNameError,
      selectedRegion: selectedRegionError,
    });

    // If there are no errors, submit the form
    if (!roleError && !bucketNameError && !selectedRegionError) {
      const memberAccountNumbers = getMemberAccountNumbers ? getMemberAccountNumbers() : "";
      
      const featureConfigurationRequest = {
        accountNumber: masterAccountNumber,
        role: role,
        bucketName: bucketName,
        selectedRegion: selectedRegion,
        module: "Event Insight",
        readOnlyRole: masterAccountDetails?.roleSessionName || "",
        externalId: masterAccountDetails?.externalId || "",
        memberAccountNumbers: memberAccountNumbers,
      };

      console.log("🚀 ~ EventInsightConfigFields ~ memberAccountNumbers:", memberAccountNumbers);
      console.log("🚀 ~ EventInsightConfigFields ~ featureConfigurationRequest:", featureConfigurationRequest);

      console.log("featureConfigurationRequest = ", featureConfigurationRequest);
      props.postFeatureConfiguration(featureConfigurationRequest);
    }
  };

  // Handle success and error responses
  useEffect(() => {
    if (props.postFeatureConfigurationMessage) {
      // Show success message in UI instead of alert
      setErrors((prevErrors) => ({
        ...prevErrors,
        success: "Event Insight successfully enabled for master and selected member accounts",
      }));
      
      // Call onSuccess callback after a short delay to show the success message
      setTimeout(() => {
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      }, 2000); // Show success message for 2 seconds before closing
    }
  }, [props.postFeatureConfigurationMessage, onSuccess, props]);

  useEffect(() => {
    if (props.postFeatureConfigurationError) {
      // Set error in the form state to display in UI
      let errorMessage = props.postFeatureConfigurationError;
      
      // If it's a technical error, provide a more user-friendly message
      if (errorMessage.includes("Cannot read properties of undefined") || 
          errorMessage.includes("reading 'data'")) {
        errorMessage = "Unable to complete the verification. Please try again after a while. If the issue persists, please contact Support at support.cloudcatcher@cloudnovatech.com";
      }
      
      setErrors((prevErrors) => ({
        ...prevErrors,
        submit: errorMessage,
      }));
    }
  }, [props.postFeatureConfigurationError]);

  return (
    <Box mt={3} p={3} border={1} borderColor="grey.200" borderRadius={3} backgroundColor="white" boxShadow={1} sx={{ width: '100%' }}>
      <Typography variant="h6" fontWeight={600} mb={3} color="text.primary">
        Configure Cloud Event
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={6}>
        Configure the Cloud Event settings for your master account to enable cloud event monitoring.
      </Typography>

      <Box mb={4}></Box>

      <form onSubmit={handleSubmit}>
        <Box mb={3}>
          <TextField
            fullWidth
            label="IAM Role*"
            value={role}
            onChange={handleRoleChange}
            error={!!errors.role}
            helperText={errors.role}
            placeholder="arn:aws:iam::123456789012:role/YourRoleName"
            size="small"
          />
        </Box>

        <Box mb={3}>
          <TextField
            fullWidth
            label="S3 Bucket ARN*"
            value={bucketName}
            onChange={handleBucketNameChange}
            error={!!errors.bucketName}
            helperText={errors.bucketName}
            placeholder="arn:aws:s3:::your-bucket-name"
            size="small"
          />
        </Box>

        <Box mb={3}>
          <FormControl fullWidth size="small" error={!!errors.selectedRegion}>
            <InputLabel>AWS Region*</InputLabel>
            <Select
              value={selectedRegion}
              onChange={handleRegionChange}
              label="AWS Region*"
            >
              <MenuItem value="">
                <em>Choose a region</em>
              </MenuItem>
              {awsRegions.list().map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name} — {region.code}
                </MenuItem>
              ))}
            </Select>
            {errors.selectedRegion && (
              <FormHelperText>{errors.selectedRegion}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {/* Error and Success display */}
        {errors.submit && (
          <Box mb={2}>
            <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
              {errors.submit}
            </Typography>
          </Box>
        )}
        
        {errors.success && (
          <Box mb={2}>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
              {errors.success}
            </Typography>
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            type="submit"
            variant="contained"
            disabled={props.postFeatureConfigurationLoading || !isFormValid()}
            style={{
              backgroundColor: isFormValid() && !props.postFeatureConfigurationLoading ? "#F98125" : "#ccc",
              color: "#fff",
              minWidth: "120px",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 500,
            }}
            sx={{
              "&:hover": {
                backgroundColor: isFormValid() && !props.postFeatureConfigurationLoading ? "#f37200" : "#ccc",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#666",
              },
            }}
          >
            {props.postFeatureConfigurationLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} style={{ color: "#fff" }} />
                <span>Verifying...</span>
              </Box>
            ) : errors.submit ? (
              "Try Again"
            ) : (
              "Verify"
            )}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    postFeatureConfigurationMessage: state.featureConfiguration.postFeatureConfigurationMessage,
    postFeatureConfigurationError: state.featureConfiguration.postFeatureConfigurationError,
    postFeatureConfigurationLoading: state.featureConfiguration.isPostingFeatureConfiguration,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postFeatureConfiguration: (payload) => {
      dispatch(postFeatureConfiguration(payload));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventInsightConfigFields); 